import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Chip,
  Grid,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { Save, Cancel, Add, Delete, Remove, CloudUpload, Download, OpenInNew, InsertDriveFile } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useEditMode } from '../context/EditModeContext';

// En producción, usar URL relativa (Nginx redirige /api/ al backend)
// En desarrollo, usar localhost:5000
// Si REACT_APP_API_URL está configurado pero es HTTP, usar ruta relativa en HTTPS
let API_URL = process.env.REACT_APP_API_URL || '';
if (API_URL && API_URL.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
  // Si estamos en HTTPS pero API_URL es HTTP, usar ruta relativa
  API_URL = '';
} else if (!API_URL && process.env.NODE_ENV !== 'production') {
  API_URL = 'http://localhost:5000';
}

function PropiedadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { editMode } = useEditMode();
  const [propiedad, setPropiedad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [nombreEditado, setNombreEditado] = useState('');
  const [presupuestoEditado, setPresupuestoEditado] = useState({
    fecha: null,
    monto: '',
    moneda: '',
    cuotas: ''
  });
  const [fechaPresentacion, setFechaPresentacion] = useState(null);
  const [fechaProgramada, setFechaProgramada] = useState(null);
  const [laboratorio, setLaboratorio] = useState({
    presupuesto: { numero: '', fecha: null },
    factura: { numero: '', fecha: null }
  });
  const [adelantos, setAdelantos] = useState([]);
  const [muestras, setMuestras] = useState({
    agua: 0,
    aire: 0,
    suelo: 0,
    limnologia: 0,
    f_y_f: 0,
    otras: []
  });
  const [dialogMuestraAbierto, setDialogMuestraAbierto] = useState(false);
  const [nuevaMuestra, setNuevaMuestra] = useState({ nombre: '', cantidad: '' });
  const [archivos, setArchivos] = useState({
    laboratorio: {
      cadena_custodia: null,
      presupuesto: null,
      factura: null
    },
    laboratorio_extras: [],
    otros: [
      { id: 'presupuesto', nombre: 'Presupuesto', archivo: null, esDefault: true },
      { id: 'plan_monitoreo', nombre: 'Plan de Monitoreo', archivo: null, esDefault: true },
      { id: 'factura_1', nombre: 'Factura 1', archivo: null, esDefault: true },
      { id: 'factura_2', nombre: 'Factura 2', archivo: null, esDefault: true }
    ],
    invitaciones: {
      mineria: null,
      recursos_hidricos: null,
      medio_ambiente: null
    },
    comunidades: []
  });
  
  // Estados para drag & drop visual feedback
  const [dragOver, setDragOver] = useState({
    otros: {},
    laboratorio: {},
    invitaciones: {},
    comunidades: {}
  });
  
  // Estados para loading de uploads y downloads
  const [uploading, setUploading] = useState({
    otros: {},
    laboratorio: {},
    invitaciones: {},
    comunidades: {}
  });
  
  const [downloading, setDownloading] = useState({
    otros: {},
    laboratorio: {},
    invitaciones: {},
    comunidades: {}
  });

  useEffect(() => {
    cargarPropiedad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cargarPropiedad = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/propiedades`);
      const propiedadEncontrada = response.data.find(p => p.id === parseInt(id));
      
      if (propiedadEncontrada) {
        setPropiedad(propiedadEncontrada);
        setNombreEditado(propiedadEncontrada.nombre);
        // Inicializar presupuesto
        const presupuesto = propiedadEncontrada.presupuesto || {};
        setPresupuestoEditado({
          fecha: presupuesto.fecha ? dayjs(presupuesto.fecha) : null,
          monto: presupuesto.monto || '',
          moneda: presupuesto.moneda || '',
          cuotas: presupuesto.cuotas || ''
        });
        // Inicializar fechas
        setFechaPresentacion(propiedadEncontrada.fecha_presentacion ? dayjs(propiedadEncontrada.fecha_presentacion) : null);
        setFechaProgramada(propiedadEncontrada.fecha_programada ? dayjs(propiedadEncontrada.fecha_programada) : null);
        // Inicializar laboratorio
        const lab = propiedadEncontrada.laboratorio || {};
        setLaboratorio({
          presupuesto: {
            numero: lab.presupuesto?.numero || '',
            fecha: lab.presupuesto?.fecha ? dayjs(lab.presupuesto.fecha) : null
          },
          factura: {
            numero: lab.factura?.numero || '',
            fecha: lab.factura?.fecha ? dayjs(lab.factura.fecha) : null
          }
        });
        // Inicializar adelantos
        const adelantosData = propiedadEncontrada.adelantos || [];
        if (adelantosData.length === 0) {
          // Si no hay adelantos, crear uno vacío por defecto
          setAdelantos([{ monto: '', fecha: null }]);
        } else {
          setAdelantos(adelantosData.map(adelanto => ({
            monto: adelanto.monto || '',
            fecha: adelanto.fecha ? dayjs(adelanto.fecha) : null
          })));
        }
        // Inicializar muestras
        const muestrasData = propiedadEncontrada.muestras || {};
        setMuestras({
          agua: muestrasData.agua || 0,
          aire: muestrasData.aire || 0,
          suelo: muestrasData.suelo || 0,
          limnologia: muestrasData.limnologia || 0,
          f_y_f: muestrasData.f_y_f || 0,
          otras: muestrasData.otras || []
        });
        // Inicializar archivos
        const archivosData = propiedadEncontrada.archivos || {};
        
        // Inicializar laboratorio preservando valores guardados
        const laboratorioGuardado = archivosData.laboratorio || {};
        const laboratorioInicial = {
          cadena_custodia: laboratorioGuardado.cadena_custodia || null,
          presupuesto: laboratorioGuardado.presupuesto || null,
          factura: laboratorioGuardado.factura || null
        };
        const laboratorioExtrasInicial = archivosData.laboratorio_extras || [];
        
        // Inicializar otros preservando valores guardados y agregando defaults si faltan
        const otrosGuardados = archivosData.otros || [];
        const otrosDefaults = [
          { id: 'presupuesto', nombre: 'Presupuesto', archivo: null, esDefault: true },
          { id: 'plan_monitoreo', nombre: 'Plan de Monitoreo', archivo: null, esDefault: true },
          { id: 'factura_1', nombre: 'Factura 1', archivo: null, esDefault: true },
          { id: 'factura_2', nombre: 'Factura 2', archivo: null, esDefault: true }
        ];
        
        // Combinar defaults con guardados (preservar defaults, agregar guardados que no sean defaults)
        const otrosInicial = [...otrosDefaults];
        otrosGuardados.forEach(otroGuardado => {
          if (!otroGuardado.esDefault) {
            otrosInicial.push(otroGuardado);
          } else {
            // Actualizar el default con el valor guardado
            const defaultIndex = otrosInicial.findIndex(d => d.id === otroGuardado.id);
            if (defaultIndex !== -1) {
              otrosInicial[defaultIndex] = otroGuardado;
            }
          }
        });
        
        // Inicializar invitaciones preservando valores guardados
        const invitacionesGuardadas = archivosData.invitaciones || {};
        const invitacionesInicial = {
          mineria: invitacionesGuardadas.mineria || null,
          recursos_hidricos: invitacionesGuardadas.recursos_hidricos || null,
          medio_ambiente: invitacionesGuardadas.medio_ambiente || null
        };
        
        // Inicializar comunidades preservando valores guardados
        const comunidadesInicial = archivosData.comunidades || [];
        
        setArchivos({
          laboratorio: laboratorioInicial,
          laboratorio_extras: laboratorioExtrasInicial,
          otros: otrosInicial,
          invitaciones: invitacionesInicial,
          comunidades: comunidadesInicial
        });
        
        console.log('Archivos cargados desde backend:', {
          laboratorio: laboratorioInicial,
          otros: otrosInicial,
          invitaciones: invitacionesInicial,
          comunidades: comunidadesInicial
        });
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error cargando propiedad:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async () => {
    if (!nombreEditado.trim()) {
      alert('El nombre no puede estar vacío');
      return;
    }

    try {
      const presupuestoData = {
        fecha: presupuestoEditado.fecha ? presupuestoEditado.fecha.format('YYYY-MM-DD') : null,
        monto: presupuestoEditado.monto ? parseInt(presupuestoEditado.monto) : null,
        moneda: presupuestoEditado.moneda || null,
        cuotas: presupuestoEditado.cuotas ? parseInt(presupuestoEditado.cuotas) : null
      };

      const laboratorioData = {
        presupuesto: {
          numero: laboratorio.presupuesto.numero ? parseInt(laboratorio.presupuesto.numero) : null,
          fecha: laboratorio.presupuesto.fecha ? laboratorio.presupuesto.fecha.format('YYYY-MM-DD') : null
        },
        factura: {
          numero: laboratorio.factura.numero ? parseInt(laboratorio.factura.numero) : null,
          fecha: laboratorio.factura.fecha ? laboratorio.factura.fecha.format('YYYY-MM-DD') : null
        }
      };

      const adelantosData = adelantos
        .filter(adelanto => adelanto.monto || adelanto.fecha)
        .map(adelanto => ({
          monto: adelanto.monto ? parseFloat(adelanto.monto) : null,
          fecha: adelanto.fecha ? adelanto.fecha.format('YYYY-MM-DD') : null
        }));

      const muestrasData = {
        agua: parseInt(muestras.agua) || 0,
        aire: parseInt(muestras.aire) || 0,
        suelo: parseInt(muestras.suelo) || 0,
        limnologia: parseInt(muestras.limnologia) || 0,
        f_y_f: parseInt(muestras.f_y_f) || 0,
        otras: muestras.otras
          .filter(otra => otra.nombre || otra.cantidad)
          .map(otra => ({
            nombre: otra.nombre || '',
            cantidad: otra.cantidad ? parseInt(otra.cantidad) : 0
          }))
      };

      const response = await axios.put(`${API_URL}/api/propiedades/${id}`, {
        nombre: nombreEditado.trim(),
        presupuesto: presupuestoData,
        fecha_presentacion: fechaPresentacion ? fechaPresentacion.format('YYYY-MM-DD') : null,
        fecha_programada: fechaProgramada ? fechaProgramada.format('YYYY-MM-DD') : null,
        laboratorio: laboratorioData,
        adelantos: adelantosData,
        muestras: muestrasData
      });
      setPropiedad(response.data);
      // Actualizar el estado del presupuesto con los valores guardados
      const presupuestoGuardado = response.data.presupuesto || {};
      setPresupuestoEditado({
        fecha: presupuestoGuardado.fecha ? dayjs(presupuestoGuardado.fecha) : null,
        monto: presupuestoGuardado.monto || '',
        moneda: presupuestoGuardado.moneda || '',
        cuotas: presupuestoGuardado.cuotas || ''
      });
      setFechaPresentacion(response.data.fecha_presentacion ? dayjs(response.data.fecha_presentacion) : null);
      setFechaProgramada(response.data.fecha_programada ? dayjs(response.data.fecha_programada) : null);
      const lab = response.data.laboratorio || {};
      setLaboratorio({
        presupuesto: {
          numero: lab.presupuesto?.numero || '',
          fecha: lab.presupuesto?.fecha ? dayjs(lab.presupuesto.fecha) : null
        },
        factura: {
          numero: lab.factura?.numero || '',
          fecha: lab.factura?.fecha ? dayjs(lab.factura.fecha) : null
        }
      });
      const adelantosGuardados = response.data.adelantos || [];
      if (adelantosGuardados.length === 0) {
        setAdelantos([{ monto: '', fecha: null }]);
      } else {
        setAdelantos(adelantosGuardados.map(adelanto => ({
          monto: adelanto.monto || '',
          fecha: adelanto.fecha ? dayjs(adelanto.fecha) : null
        })));
      }
      const muestrasGuardadas = response.data.muestras || {};
      setMuestras({
        agua: muestrasGuardadas.agua || 0,
        aire: muestrasGuardadas.aire || 0,
        suelo: muestrasGuardadas.suelo || 0,
        limnologia: muestrasGuardadas.limnologia || 0,
        f_y_f: muestrasGuardadas.f_y_f || 0,
        otras: muestrasGuardadas.otras || []
      });
      const archivosGuardados = response.data.archivos || {};
      const laboratorioGuardado = archivosGuardados.laboratorio || {};
      const laboratorioInicial = {
        cadena_custodia: laboratorioGuardado.cadena_custodia || null,
        presupuesto: laboratorioGuardado.presupuesto || null,
        factura: laboratorioGuardado.factura || null
      };
      const laboratorioExtrasInicial = archivosGuardados.laboratorio_extras || [];
      setArchivos({
        laboratorio: laboratorioInicial,
        laboratorio_extras: laboratorioExtrasInicial,
        otros: archivosGuardados.otros || [
          { id: 'presupuesto', nombre: 'Presupuesto', archivo: null, esDefault: true },
          { id: 'plan_monitoreo', nombre: 'Plan de Monitoreo', archivo: null, esDefault: true },
          { id: 'factura_1', nombre: 'Factura 1', archivo: null, esDefault: true },
          { id: 'factura_2', nombre: 'Factura 2', archivo: null, esDefault: true }
        ],
        invitaciones: archivosGuardados.invitaciones || {
          mineria: null,
          recursos_hidricos: null,
          medio_ambiente: null
        },
        comunidades: archivosGuardados.comunidades || []
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error actualizando propiedad:', error);
      alert('Error al actualizar la propiedad minera');
    }
  };

  const handleCancelar = () => {
    setNombreEditado(propiedad.nombre);
    const presupuesto = propiedad.presupuesto || {};
    setPresupuestoEditado({
      fecha: presupuesto.fecha ? dayjs(presupuesto.fecha) : null,
      monto: presupuesto.monto || '',
      moneda: presupuesto.moneda || '',
      cuotas: presupuesto.cuotas || ''
    });
    setFechaPresentacion(propiedad.fecha_presentacion ? dayjs(propiedad.fecha_presentacion) : null);
    setFechaProgramada(propiedad.fecha_programada ? dayjs(propiedad.fecha_programada) : null);
    const lab = propiedad.laboratorio || {};
    setLaboratorio({
      presupuesto: {
        numero: lab.presupuesto?.numero || '',
        fecha: lab.presupuesto?.fecha ? dayjs(lab.presupuesto.fecha) : null
      },
      factura: {
        numero: lab.factura?.numero || '',
        fecha: lab.factura?.fecha ? dayjs(lab.factura.fecha) : null
      }
    });
    const adelantosData = propiedad.adelantos || [];
    if (adelantosData.length === 0) {
      setAdelantos([{ monto: '', fecha: null }]);
    } else {
      setAdelantos(adelantosData.map(adelanto => ({
        monto: adelanto.monto || '',
        fecha: adelanto.fecha ? dayjs(adelanto.fecha) : null
      })));
    }
    const muestrasData = propiedad.muestras || {};
    setMuestras({
      agua: muestrasData.agua || 0,
      aire: muestrasData.aire || 0,
      suelo: muestrasData.suelo || 0,
      limnologia: muestrasData.limnologia || 0,
      f_y_f: muestrasData.f_y_f || 0,
      otras: muestrasData.otras || []
    });
    setIsEditing(false);
  };

  useEffect(() => {
    if (propiedad && editMode && !isEditing) {
      setIsEditing(true);
    } else if (!editMode && isEditing) {
      setIsEditing(false);
      if (propiedad) {
        setNombreEditado(propiedad.nombre);
        // Restaurar presupuesto desde la propiedad guardada
        const presupuesto = propiedad.presupuesto || {};
        setPresupuestoEditado({
          fecha: presupuesto.fecha ? dayjs(presupuesto.fecha) : null,
          monto: presupuesto.monto || '',
          moneda: presupuesto.moneda || '',
          cuotas: presupuesto.cuotas || ''
        });
        setFechaPresentacion(propiedad.fecha_presentacion ? dayjs(propiedad.fecha_presentacion) : null);
        setFechaProgramada(propiedad.fecha_programada ? dayjs(propiedad.fecha_programada) : null);
        const lab = propiedad.laboratorio || {};
        setLaboratorio({
          presupuesto: {
            numero: lab.presupuesto?.numero || '',
            fecha: lab.presupuesto?.fecha ? dayjs(lab.presupuesto.fecha) : null
          },
          factura: {
            numero: lab.factura?.numero || '',
            fecha: lab.factura?.fecha ? dayjs(lab.factura.fecha) : null
          }
        });
        const adelantosData = propiedad.adelantos || [];
        if (adelantosData.length === 0) {
          setAdelantos([{ monto: '', fecha: null }]);
        } else {
          setAdelantos(adelantosData.map(adelanto => ({
            monto: adelanto.monto || '',
            fecha: adelanto.fecha ? dayjs(adelanto.fecha) : null
          })));
        }
        const muestrasData = propiedad.muestras || {};
        setMuestras({
          agua: muestrasData.agua || 0,
          aire: muestrasData.aire || 0,
          suelo: muestrasData.suelo || 0,
          limnologia: muestrasData.limnologia || 0,
          f_y_f: muestrasData.f_y_f || 0,
          otras: muestrasData.otras || []
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!propiedad) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: 3 }}>
      {editMode && isEditing && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Nombre de la propiedad"
              value={nombreEditado}
              onChange={(e) => setNombreEditado(e.target.value)}
              sx={{ flexGrow: 1 }}
              autoFocus
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="primary" onClick={handleGuardar}>
                <Save />
              </IconButton>
              <IconButton onClick={handleCancelar}>
                <Cancel />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
      {editMode && !isEditing && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Chip 
            label="Modo Edición - Haz clic en el ícono de lápiz en la toolbar para editar el nombre" 
            color="warning" 
            size="small"
          />
        </Box>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Sección Presupuesto */}
          <Box sx={{ flex: { md: '0 0 70%' }, width: { xs: '100%', md: '70%' } }}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.95rem' }}>
                Presupuesto
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <DatePicker
                    label="Fecha"
                    value={presupuestoEditado.fecha}
                    onChange={(newValue) => setPresupuestoEditado({...presupuestoEditado, fecha: newValue})}
                    format="DD/MM/YYYY"
                    disabled={!editMode}
                    slots={{
                      textField: TextField
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        size: 'small'
                      },
                      inputAdornment: {
                        sx: {
                          display: presupuestoEditado.fecha ? 'none' : 'flex'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Monto"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={presupuestoEditado.monto}
                    onChange={(e) => setPresupuestoEditado({...presupuestoEditado, monto: e.target.value})}
                    disabled={!editMode}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    select
                    label="Moneda"
                    variant="outlined"
                    size="small"
                    value={presupuestoEditado.moneda}
                    onChange={(e) => setPresupuestoEditado({...presupuestoEditado, moneda: e.target.value})}
                    disabled={!editMode}
                  >
                    <MenuItem value="$">$</MenuItem>
                    <MenuItem value="U$D">U$D</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    select
                    label="Cuotas"
                    variant="outlined"
                    size="small"
                    value={presupuestoEditado.cuotas}
                    onChange={(e) => setPresupuestoEditado({...presupuestoEditado, cuotas: e.target.value})}
                    disabled={!editMode}
                  >
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Sección Fecha de Presentación */}
          <Box sx={{ flex: { md: '0 0 15%' }, width: { xs: '100%', md: '15%' } }}>
            <Paper elevation={2} sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1, fontSize: '0.9rem' }}>
                Fecha de Presentación
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <DatePicker
                label="Fecha de Presentación"
                value={fechaPresentacion}
                onChange={(newValue) => setFechaPresentacion(newValue)}
                format="DD/MM/YYYY"
                disabled={!editMode}
                slots={{
                  textField: TextField
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small'
                  },
                  inputAdornment: {
                    sx: {
                      display: fechaPresentacion ? 'none' : 'flex'
                    }
                  }
                }}
              />
            </Paper>
          </Box>

          {/* Sección Fecha Programada */}
          <Box sx={{ flex: { md: '0 0 15%' }, width: { xs: '100%', md: '15%' } }}>
            <Paper elevation={2} sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1, fontSize: '0.9rem' }}>
                Fecha Programada
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <DatePicker
                label="Fecha Programada"
                value={fechaProgramada}
                onChange={(newValue) => setFechaProgramada(newValue)}
                format="DD/MM/YYYY"
                disabled={!editMode}
                slots={{
                  textField: TextField
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small'
                  },
                  inputAdornment: {
                    sx: {
                      display: fechaProgramada ? 'none' : 'flex'
                    }
                  }
                }}
              />
            </Paper>
          </Box>
        </Box>

        {/* Segundo renglón - Laboratorio, Adelantos, Saldo, Muestras */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: { md: muestras.otras.length > 0 ? '0 0 19%' : '0 0 23%' }, width: { xs: '100%', md: muestras.otras.length > 0 ? '19%' : '23%' } }}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.95rem', textAlign: 'center' }}>
                  Laboratorio
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                
                {/* Presupuesto Laboratorio */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, fontSize: '0.85rem' }}>
                    Presupuesto
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={7}>
                      <TextField
                        fullWidth
                        label="Número"
                        type="number"
                        variant="outlined"
                        size="small"
                        value={laboratorio.presupuesto.numero}
                        onChange={(e) => setLaboratorio({
                          ...laboratorio,
                          presupuesto: { ...laboratorio.presupuesto, numero: e.target.value }
                        })}
                        disabled={!editMode}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <DatePicker
                        label="Fecha"
                        value={laboratorio.presupuesto.fecha}
                        onChange={(newValue) => setLaboratorio({
                          ...laboratorio,
                          presupuesto: { ...laboratorio.presupuesto, fecha: newValue }
                        })}
                        format="DD/MM/YYYY"
                        disabled={!editMode}
                        slots={{
                          textField: TextField
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            size: 'small'
                          },
                          inputAdornment: {
                            sx: {
                              display: laboratorio.presupuesto.fecha ? 'none' : 'flex'
                            }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Factura Laboratorio */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, fontSize: '0.85rem' }}>
                    Factura
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={7}>
                      <TextField
                        fullWidth
                        label="Número"
                        type="number"
                        variant="outlined"
                        size="small"
                        value={laboratorio.factura.numero}
                        onChange={(e) => setLaboratorio({
                          ...laboratorio,
                          factura: { ...laboratorio.factura, numero: e.target.value }
                        })}
                        disabled={!editMode}
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <DatePicker
                        label="Fecha"
                        value={laboratorio.factura.fecha}
                        onChange={(newValue) => setLaboratorio({
                          ...laboratorio,
                          factura: { ...laboratorio.factura, fecha: newValue }
                        })}
                        format="DD/MM/YYYY"
                        disabled={!editMode}
                        slots={{
                          textField: TextField
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            size: 'small'
                          },
                          inputAdornment: {
                            sx: {
                              display: laboratorio.factura.fecha ? 'none' : 'flex'
                            }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Box>

            {/* Sección Adelantos */}
            <Box sx={{ flex: { md: muestras.otras.length > 0 ? '0 0 19%' : '0 0 23%' }, width: { xs: '100%', md: muestras.otras.length > 0 ? '19%' : '23%' } }}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.95rem', textAlign: 'center' }}>
                  Adelantos
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                
                {adelantos.map((adelanto, index) => (
                  <Box key={index} sx={{ mb: index < adelantos.length - 1 ? 1.5 : 0 }}>
                    <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                      <Grid item xs={editMode ? 6 : 7}>
                        <TextField
                          fullWidth
                          label="Monto"
                          type="number"
                          variant="outlined"
                          size="small"
                          value={adelanto.monto}
                          onChange={(e) => {
                            const nuevosAdelantos = [...adelantos];
                            nuevosAdelantos[index] = { ...nuevosAdelantos[index], monto: e.target.value };
                            setAdelantos(nuevosAdelantos);
                          }}
                          disabled={!editMode}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={editMode ? 4 : 5}>
                        <DatePicker
                          label="Fecha"
                          value={adelanto.fecha}
                          onChange={(newValue) => {
                            const nuevosAdelantos = [...adelantos];
                            nuevosAdelantos[index] = { ...nuevosAdelantos[index], fecha: newValue };
                            setAdelantos(nuevosAdelantos);
                          }}
                          format="DD/MM/YYYY"
                          disabled={!editMode}
                          slots={{
                            textField: TextField
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'outlined',
                              size: 'small'
                            },
                            inputAdornment: {
                              sx: {
                                display: adelanto.fecha ? 'none' : 'flex'
                              }
                            }
                          }}
                        />
                      </Grid>
                      {editMode && (
                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const nuevosAdelantos = adelantos.filter((_, i) => i !== index);
                              if (nuevosAdelantos.length === 0) {
                                setAdelantos([{ monto: '', fecha: null }]);
                              } else {
                                setAdelantos(nuevosAdelantos);
                              }
                            }}
                            disabled={adelantos.length === 1}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
                
                {editMode && (
                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => setAdelantos([...adelantos, { monto: '', fecha: null }])}
                    >
                      <Add />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Sección Saldo */}
            <Box sx={{ flex: { md: '0 0 12%' }, width: { xs: '100%', md: '12%' } }}>
              <Paper elevation={2} sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1, fontSize: '0.9rem', textAlign: 'center' }}>
                  Saldo
                </Typography>
                <Divider sx={{ mb: 1 }} />
                
                {(() => {
                  // Calcular saldo: monto del presupuesto - suma de montos de adelantos
                  const montoPresupuesto = parseFloat(presupuestoEditado.monto) || 0;
                  const sumaAdelantos = adelantos.reduce((suma, adelanto) => {
                    return suma + (parseFloat(adelanto.monto) || 0);
                  }, 0);
                  const saldo = montoPresupuesto - sumaAdelantos;
                  
                  return (
                    <TextField
                      fullWidth
                      type="number"
                      variant="outlined"
                      size="small"
                      value={saldo.toFixed(2)}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 500,
                          color: saldo < 0 ? 'error.main' : 'text.primary'
                        }
                      }}
                    />
                  );
                })()}
              </Paper>
            </Box>

            {/* Sección Muestras */}
            <Box sx={{ flex: { md: muestras.otras.length > 0 ? '0 0 50%' : '0 0 42%' }, width: { xs: '100%', md: muestras.otras.length > 0 ? '50%' : '42%' } }}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.95rem', textAlign: 'center' }}>
                  Muestras
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                
                {/* Campos fijos de muestras y muestras adicionales */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: editMode ? 1.5 : 0 }}>
                  {/* Muestras fijas */}
                  {[
                    { key: 'agua', label: 'Agua' },
                    { key: 'aire', label: 'Aire' },
                    { key: 'suelo', label: 'Suelo' },
                    { key: 'limnologia', label: 'Limnología' },
                    { key: 'f_y_f', label: 'F y F' }
                  ].map((tipo) => (
                    <Box key={tipo.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 1 auto', minWidth: 'fit-content' }}>
                      <TextField
                        label={tipo.label}
                        type="number"
                        variant="outlined"
                        size="small"
                        value={muestras[tipo.key]}
                        onChange={(e) => setMuestras({
                          ...muestras,
                          [tipo.key]: parseInt(e.target.value) || 0
                        })}
                        disabled={!editMode}
                        inputProps={{ min: 0, step: 1, style: { fontSize: '1.1rem', fontWeight: 500, textAlign: 'center', width: '60px' } }}
                        InputLabelProps={{
                          sx: {
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: 'text.primary'
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            width: 'auto',
                            minWidth: '120px'
                          }
                        }}
                      />
                      {editMode && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => setMuestras({
                              ...muestras,
                              [tipo.key]: (muestras[tipo.key] || 0) + 1
                            })}
                            sx={{ minWidth: 'auto', width: 32, height: 32 }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setMuestras({
                              ...muestras,
                              [tipo.key]: Math.max(0, (muestras[tipo.key] || 0) - 1)
                            })}
                            sx={{ minWidth: 'auto', width: 32, height: 32 }}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Muestras adicionales */}
                  {muestras.otras.map((otra, index) => (
                    <Box key={`otra-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 1 auto', minWidth: 'fit-content' }}>
                      {editMode && (
                        <TextField
                          placeholder="Nombre"
                          variant="outlined"
                          size="small"
                          value={otra.nombre || ''}
                          onChange={(e) => {
                            const nuevasOtras = [...muestras.otras];
                            nuevasOtras[index] = { ...nuevasOtras[index], nombre: e.target.value };
                            setMuestras({ ...muestras, otras: nuevasOtras });
                          }}
                          sx={{ width: 120 }}
                        />
                      )}
                      <TextField
                        label={otra.nombre || 'Muestra'}
                        type="number"
                        variant="outlined"
                        size="small"
                        value={otra.cantidad || ''}
                        onChange={(e) => {
                          const nuevasOtras = [...muestras.otras];
                          nuevasOtras[index] = { ...nuevasOtras[index], cantidad: e.target.value };
                          setMuestras({ ...muestras, otras: nuevasOtras });
                        }}
                        disabled={!editMode}
                        inputProps={{ min: 0, step: 1, style: { fontSize: '1.1rem', fontWeight: 500, textAlign: 'center', width: '60px' } }}
                        InputLabelProps={{
                          sx: {
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: 'text.primary'
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            width: 'auto',
                            minWidth: '120px'
                          }
                        }}
                      />
                      {editMode && (
                        <>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const nuevasOtras = [...muestras.otras];
                                nuevasOtras[index] = { ...nuevasOtras[index], cantidad: (parseInt(nuevasOtras[index].cantidad) || 0) + 1 };
                                setMuestras({ ...muestras, otras: nuevasOtras });
                              }}
                              sx={{ minWidth: 'auto', width: 32, height: 32 }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const nuevasOtras = [...muestras.otras];
                                nuevasOtras[index] = { ...nuevasOtras[index], cantidad: Math.max(0, (parseInt(nuevasOtras[index].cantidad) || 0) - 1) };
                                setMuestras({ ...muestras, otras: nuevasOtras });
                              }}
                              sx={{ minWidth: 'auto', width: 32, height: 32 }}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const nuevasOtras = muestras.otras.filter((_, i) => i !== index);
                              setMuestras({ ...muestras, otras: nuevasOtras });
                            }}
                            sx={{ minWidth: 'auto', width: 32, height: 32 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>

                {editMode && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => {
                        setNuevaMuestra({ nombre: '', cantidad: '' });
                        setDialogMuestraAbierto(true);
                      }}
                    >
                      <Add />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>

          {/* Dialog para agregar nueva muestra */}
          <Dialog 
            open={dialogMuestraAbierto} 
            onClose={() => setDialogMuestraAbierto(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Agregar Nueva Muestra</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Nombre de la muestra"
                  variant="outlined"
                  value={nuevaMuestra.nombre}
                  onChange={(e) => setNuevaMuestra({ ...nuevaMuestra, nombre: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && nuevaMuestra.nombre.trim()) {
                      e.preventDefault();
                      handleAgregarMuestra();
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  variant="outlined"
                  value={nuevaMuestra.cantidad}
                  onChange={(e) => setNuevaMuestra({ ...nuevaMuestra, cantidad: e.target.value })}
                  inputProps={{ min: 0, step: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && nuevaMuestra.nombre.trim()) {
                      e.preventDefault();
                      handleAgregarMuestra();
                    }
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogMuestraAbierto(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAgregarMuestra}
                variant="contained"
                disabled={!nuevaMuestra.nombre.trim()}
              >
                Agregar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Sección ARCHIVOS */}
          <Box sx={{ mt: 2, width: '100%' }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" component="h2" gutterBottom sx={{ fontWeight: 500, mb: 3, fontSize: '1rem' }}>
                ARCHIVOS
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {/* Subsección Otros */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.2rem', textAlign: 'center' }}>
                  Otros
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {archivos.otros.map((otro, index) => (
                    <Grid item xs={12} md={4} key={otro.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          border: '2px dashed',
                          borderColor: dragOver.otros[otro.id] 
                            ? 'primary.main' 
                            : otro.archivo 
                              ? 'primary.main' 
                              : 'grey.300',
                          backgroundColor: dragOver.otros[otro.id]
                            ? 'primary.light'
                            : otro.archivo 
                              ? 'action.hover' 
                              : 'background.paper',
                          cursor: uploading.otros[otro.id] || downloading.otros[otro.id] ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: uploading.otros[otro.id] || downloading.otros[otro.id] ? 0.7 : 1,
                          '&:hover': {
                            borderColor: dragOver.otros[otro.id] ? 'primary.main' : 'primary.main',
                            backgroundColor: dragOver.otros[otro.id] ? 'primary.light' : 'action.hover'
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            otros: { ...prev.otros, [otro.id]: true }
                          }));
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            otros: { ...prev.otros, [otro.id]: false }
                          }));
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            otros: { ...prev.otros, [otro.id]: false }
                          }));
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleFileUploadOtros(files[0], otro.id);
                          }
                        }}
                        onClick={() => {
                          if (!otro.archivo && !uploading.otros[otro.id] && !downloading.otros[otro.id]) {
                            document.getElementById(`file-input-otros-${otro.id}`).click();
                          }
                        }}
                      >
                        {!otro.esDefault && editMode && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOtro(otro.id);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        {/* Loading overlay */}
                        {(uploading.otros[otro.id] || downloading.otros[otro.id]) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                            gap: 1
                          }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="primary">
                              {uploading.otros[otro.id] ? 'Subiendo...' : 'Descargando...'}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Drag over message */}
                        {dragOver.otros[otro.id] && !otro.archivo && !uploading.otros[otro.id] && (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: '120px',
                            gap: 1
                          }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              Suelta aquí el archivo
                            </Typography>
                          </Box>
                        )}
                        
                        {otro.archivo && !dragOver.otros[otro.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                            {otro.esDefault ? (
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                {otro.nombre}
                              </Typography>
                            ) : (
                              editMode ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={otro.nombre}
                                onChange={(e) => handleUpdateNombreOtro(otro.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    guardarArchivos();
                                  }
                                }}
                                onBlur={() => guardarArchivos()}
                                placeholder="Nombre del archivo"
                                sx={{ mb: 1 }}
                              />
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                  {otro.nombre || 'Archivo'}
                                </Typography>
                              )
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {otro.archivo?.nombre_original || otro.archivo?.name || 'Archivo'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={downloading.otros[otro.id]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFileOtros(otro.id);
                                }}
                              >
                                {downloading.otros[otro.id] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFileOtros(otro.id);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFileOtros(otro.id);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : !dragOver.otros[otro.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                            {otro.esDefault ? (
                              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {otro.nombre}
                              </Typography>
                            ) : (
                              editMode ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={otro.nombre}
                                  onChange={(e) => handleUpdateNombreOtro(otro.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      guardarArchivos();
                                    }
                                  }}
                                  onBlur={() => guardarArchivos()}
                                  placeholder="Nombre del archivo"
                                  sx={{ mb: 1 }}
                                  autoFocus
                                />
                              ) : (
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                  {otro.nombre || 'Archivo'}
                                </Typography>
                              )
                            )}
                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              Arrastra un archivo aquí o haz clic para subir
                            </Typography>
                            <input
                              type="file"
                              id={`file-input-otros-${otro.id}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  handleFileUploadOtros(e.target.files[0], otro.id);
                                }
                              }}
                            />
                          </Box>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}

                  {/* Recuadro para agregar nuevo \"Otro\" (estilo Invitaciones/Comunidades) */}
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        backgroundColor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 160,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={handleAgregarOtro}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Add sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 500 }}>
                          Agregar recuadro
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Subsección Laboratorio */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.2rem', textAlign: 'center' }}>
                  Laboratorio
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {[
                    { key: 'cadena_custodia', label: 'Cadena de Custodia' },
                    { key: 'presupuesto', label: 'Presupuesto' },
                    { key: 'factura', label: 'Factura' }
                  ].map((tipo) => (
                    <Grid item xs={12} md={4} key={tipo.key}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          border: '2px dashed',
                          borderColor: dragOver.laboratorio[tipo.key] 
                            ? 'primary.main' 
                            : archivos.laboratorio[tipo.key] 
                              ? 'primary.main' 
                              : 'grey.300',
                          backgroundColor: dragOver.laboratorio[tipo.key]
                            ? 'primary.light'
                            : archivos.laboratorio[tipo.key] 
                              ? 'action.hover' 
                              : 'background.paper',
                          cursor: uploading.laboratorio[tipo.key] || downloading.laboratorio[tipo.key] ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: uploading.laboratorio[tipo.key] || downloading.laboratorio[tipo.key] ? 0.7 : 1,
                          '&:hover': {
                            borderColor: dragOver.laboratorio[tipo.key] ? 'primary.main' : 'primary.main',
                            backgroundColor: dragOver.laboratorio[tipo.key] ? 'primary.light' : 'action.hover'
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [tipo.key]: true }
                          }));
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [tipo.key]: false }
                          }));
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [tipo.key]: false }
                          }));
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleFileUpload(files[0], 'laboratorio', tipo.key);
                          }
                        }}
                        onClick={() => {
                          if (!archivos.laboratorio[tipo.key] && !uploading.laboratorio[tipo.key] && !downloading.laboratorio[tipo.key]) {
                            document.getElementById(`file-input-${tipo.key}`).click();
                          }
                        }}
                      >
                        {/* Loading overlay */}
                        {(uploading.laboratorio[tipo.key] || downloading.laboratorio[tipo.key]) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                            gap: 1
                          }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="primary">
                              {uploading.laboratorio[tipo.key] ? 'Subiendo...' : 'Descargando...'}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Drag over message */}
                        {dragOver.laboratorio[tipo.key] && !archivos.laboratorio[tipo.key] && !uploading.laboratorio[tipo.key] && (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: '120px',
                            gap: 1
                          }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              Suelta aquí el archivo
                            </Typography>
                          </Box>
                        )}
                        
                        {archivos.laboratorio[tipo.key] && !dragOver.laboratorio[tipo.key] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {archivos.laboratorio[tipo.key]?.nombre_original || archivos.laboratorio[tipo.key]?.name || 'Archivo'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={downloading.laboratorio[tipo.key]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFile('laboratorio', tipo.key);
                                }}
                              >
                                {downloading.laboratorio[tipo.key] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFile('laboratorio', tipo.key);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile('laboratorio', tipo.key);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : !dragOver.laboratorio[tipo.key] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              {tipo.label}
                            </Typography>
                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              Arrastra un archivo aquí o haz clic para subir
                            </Typography>
                            <input
                              type="file"
                              id={`file-input-${tipo.key}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  handleFileUpload(e.target.files[0], 'laboratorio', tipo.key);
                                }
                              }}
                            />
                          </Box>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}

                  {/* Recuadros dinámicos adicionales de Laboratorio */}
                  {archivos.laboratorio_extras && archivos.laboratorio_extras.map((item) => (
                    <Grid item xs={12} md={4} key={item.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          border: '2px dashed',
                          borderColor: dragOver.laboratorio[item.id]
                            ? 'primary.main'
                            : item.archivo
                              ? 'primary.main'
                              : 'grey.300',
                          backgroundColor: dragOver.laboratorio[item.id]
                            ? 'primary.light'
                            : item.archivo
                              ? 'action.hover'
                              : 'background.paper',
                          cursor: uploading.laboratorio[item.id] || downloading.laboratorio[item.id] ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: uploading.laboratorio[item.id] || downloading.laboratorio[item.id] ? 0.7 : 1,
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: dragOver.laboratorio[item.id] ? 'primary.light' : 'action.hover'
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [item.id]: true }
                          }));
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [item.id]: false }
                          }));
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            laboratorio: { ...prev.laboratorio, [item.id]: false }
                          }));
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleFileUploadLaboratorioExtra(files[0], item.id);
                          }
                        }}
                        onClick={() => {
                          if (!item.archivo && !uploading.laboratorio[item.id] && !downloading.laboratorio[item.id]) {
                            document.getElementById(`file-input-laboratorio-extra-${item.id}`).click();
                          }
                        }}
                      >
                        {/* Botón eliminar recuadro */}
                        {editMode && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLaboratorioExtra(item.id);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}

                        {/* Loading overlay */}
                        {(uploading.laboratorio[item.id] || downloading.laboratorio[item.id]) && (
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                            gap: 1
                          }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="primary">
                              {uploading.laboratorio[item.id] ? 'Subiendo...' : 'Descargando...'}
                            </Typography>
                          </Box>
                        )}

                        {/* Drag over message */}
                        {dragOver.laboratorio[item.id] && !item.archivo && !uploading.laboratorio[item.id] && (
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '120px',
                            gap: 1
                          }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              Suelta aquí el archivo
                            </Typography>
                          </Box>
                        )}

                        {item.archivo && !dragOver.laboratorio[item.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                            {editMode ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={item.nombre}
                                onChange={(e) => handleUpdateNombreLaboratorioExtra(item.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    guardarArchivos();
                                  }
                                }}
                                onBlur={() => guardarArchivos()}
                                placeholder="Nombre del archivo"
                                sx={{ mb: 1 }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                {item.nombre || 'Archivo'}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {item.archivo?.nombre_original || item.archivo?.name || 'Archivo'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={downloading.laboratorio[item.id]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFileLaboratorioExtra(item.id);
                                }}
                              >
                                {downloading.laboratorio[item.id] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFileLaboratorioExtra(item.id);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFileLaboratorioExtra(item.id);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : !dragOver.laboratorio[item.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                            {editMode ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={item.nombre}
                                onChange={(e) => handleUpdateNombreLaboratorioExtra(item.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    guardarArchivos();
                                  }
                                }}
                                onBlur={() => guardarArchivos()}
                                placeholder="Nombre del archivo"
                                sx={{ mb: 1 }}
                                autoFocus
                              />
                            ) : (
                              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {item.nombre || 'Archivo'}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              Arrastra un archivo aquí o haz clic para subir
                            </Typography>
                            <input
                              type="file"
                              id={`file-input-laboratorio-extra-${item.id}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  handleFileUploadLaboratorioExtra(e.target.files[0], item.id);
                                }
                              }}
                            />
                          </Box>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}

                  {/* Recuadro para agregar nuevo archivo de Laboratorio */}
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        backgroundColor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 160,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={handleAgregarLaboratorioExtra}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Add sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 500 }}>
                          Agregar recuadro
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Subsección Invitaciones */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.2rem', textAlign: 'center' }}>
                  Invitaciones
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { key: 'mineria', label: 'Mineria' },
                    { key: 'recursos_hidricos', label: 'Recursos Hidricos' },
                    { key: 'medio_ambiente', label: 'Medio Ambiente' }
                  ].map((tipo) => (
                    <Grid item xs={12} md={4} key={tipo.key}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          border: '2px dashed',
                          borderColor: dragOver.invitaciones[tipo.key] 
                            ? 'primary.main' 
                            : archivos.invitaciones[tipo.key] 
                              ? 'primary.main' 
                              : 'grey.300',
                          backgroundColor: dragOver.invitaciones[tipo.key]
                            ? 'primary.light'
                            : archivos.invitaciones[tipo.key] 
                              ? 'action.hover' 
                              : 'background.paper',
                          cursor: uploading.invitaciones[tipo.key] || downloading.invitaciones[tipo.key] ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: uploading.invitaciones[tipo.key] || downloading.invitaciones[tipo.key] ? 0.7 : 1,
                          '&:hover': {
                            borderColor: dragOver.invitaciones[tipo.key] ? 'primary.main' : 'primary.main',
                            backgroundColor: dragOver.invitaciones[tipo.key] ? 'primary.light' : 'action.hover'
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            invitaciones: { ...prev.invitaciones, [tipo.key]: true }
                          }));
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            invitaciones: { ...prev.invitaciones, [tipo.key]: false }
                          }));
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            invitaciones: { ...prev.invitaciones, [tipo.key]: false }
                          }));
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleFileUpload(files[0], 'invitaciones', tipo.key);
                          }
                        }}
                        onClick={() => {
                          if (!archivos.invitaciones[tipo.key] && !uploading.invitaciones[tipo.key] && !downloading.invitaciones[tipo.key]) {
                            document.getElementById(`file-input-invitaciones-${tipo.key}`).click();
                          }
                        }}
                      >
                        {/* Loading overlay */}
                        {(uploading.invitaciones[tipo.key] || downloading.invitaciones[tipo.key]) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                            gap: 1
                          }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="primary">
                              {uploading.invitaciones[tipo.key] ? 'Subiendo...' : 'Descargando...'}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Drag over message */}
                        {dragOver.invitaciones[tipo.key] && !archivos.invitaciones[tipo.key] && !uploading.invitaciones[tipo.key] && (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: '120px',
                            gap: 1
                          }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              Suelta aquí el archivo
                            </Typography>
                          </Box>
                        )}
                        
                        {archivos.invitaciones[tipo.key] && !dragOver.invitaciones[tipo.key] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {tipo.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {archivos.invitaciones[tipo.key]?.nombre_original || archivos.invitaciones[tipo.key]?.name || 'Archivo'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={downloading.invitaciones[tipo.key]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFile('invitaciones', tipo.key);
                                }}
                              >
                                {downloading.invitaciones[tipo.key] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFile('invitaciones', tipo.key);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile('invitaciones', tipo.key);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : !dragOver.invitaciones[tipo.key] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              {tipo.label}
                            </Typography>
                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              Arrastra un archivo aquí o haz clic para subir
                            </Typography>
                            <input
                              type="file"
                              id={`file-input-invitaciones-${tipo.key}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  handleFileUpload(e.target.files[0], 'invitaciones', tipo.key);
                                }
                              }}
                            />
                          </Box>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}
                  
                  {/* Comunidades dentro de Invitaciones */}
                  {archivos.comunidades.map((comunidad, index) => (
                    <Grid item xs={12} md={4} key={comunidad.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          border: '2px dashed',
                          borderColor: dragOver.comunidades[comunidad.id] 
                            ? 'primary.main' 
                            : comunidad.archivo 
                              ? 'primary.main' 
                              : 'grey.300',
                          backgroundColor: dragOver.comunidades[comunidad.id]
                            ? 'primary.light'
                            : comunidad.archivo 
                              ? 'action.hover' 
                              : 'background.paper',
                          cursor: uploading.comunidades[comunidad.id] || downloading.comunidades[comunidad.id] ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: uploading.comunidades[comunidad.id] || downloading.comunidades[comunidad.id] ? 0.7 : 1,
                          '&:hover': {
                            borderColor: dragOver.comunidades[comunidad.id] ? 'primary.main' : 'primary.main',
                            backgroundColor: dragOver.comunidades[comunidad.id] ? 'primary.light' : 'action.hover'
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            comunidades: { ...prev.comunidades, [comunidad.id]: true }
                          }));
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            comunidades: { ...prev.comunidades, [comunidad.id]: false }
                          }));
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(prev => ({
                            ...prev,
                            comunidades: { ...prev.comunidades, [comunidad.id]: false }
                          }));
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            handleFileUploadComunidades(files[0], comunidad.id);
                          }
                        }}
                        onClick={() => {
                          if (!comunidad.archivo && !uploading.comunidades[comunidad.id] && !downloading.comunidades[comunidad.id]) {
                            document.getElementById(`file-input-comunidades-${comunidad.id}`).click();
                          }
                        }}
                      >
                        {editMode && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteComunidad(comunidad.id);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        
                        {/* Loading overlay */}
                        {(uploading.comunidades[comunidad.id] || downloading.comunidades[comunidad.id]) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                            gap: 1
                          }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="primary">
                              {uploading.comunidades[comunidad.id] ? 'Subiendo...' : 'Descargando...'}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Drag over message */}
                        {dragOver.comunidades[comunidad.id] && !comunidad.archivo && !uploading.comunidades[comunidad.id] && (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: '120px',
                            gap: 1
                          }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              Suelta aquí el archivo
                            </Typography>
                          </Box>
                        )}
                        
                        {comunidad.archivo && !dragOver.comunidades[comunidad.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                            {editMode ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={comunidad.nombre}
                                onChange={(e) => handleUpdateNombreComunidad(comunidad.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    guardarArchivos();
                                  }
                                }}
                                onBlur={() => {
                                  // Guardar cuando se pierde el foco
                                  guardarArchivos();
                                }}
                                placeholder="Nombre de la comunidad"
                                sx={{ mb: 1 }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center', mb: 1 }}>
                                {comunidad.nombre || 'Comunidad'}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                              {comunidad.archivo?.nombre_original || comunidad.archivo?.name || 'Archivo'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={downloading.comunidades[comunidad.id]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFileComunidades(comunidad.id);
                                }}
                              >
                                {downloading.comunidades[comunidad.id] ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Download fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFileComunidades(comunidad.id);
                                }}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFileComunidades(comunidad.id);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : !dragOver.comunidades[comunidad.id] ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                            {editMode ? (
                              <TextField
                                fullWidth
                                size="small"
                                value={comunidad.nombre}
                                onChange={(e) => handleUpdateNombreComunidad(comunidad.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    guardarArchivos();
                                  }
                                }}
                                onBlur={() => {
                                  // Guardar cuando se pierde el foco
                                  guardarArchivos();
                                }}
                                placeholder="Nombre de la comunidad"
                                sx={{ mb: 1 }}
                                autoFocus
                              />
                            ) : (
                              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 1 }}>
                                {comunidad.nombre || 'Comunidad'}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              Arrastra un archivo aquí o haz clic para subir
                            </Typography>
                            <input
                              type="file"
                              id={`file-input-comunidades-${comunidad.id}`}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  handleFileUploadComunidades(e.target.files[0], comunidad.id);
                                }
                              }}
                            />
                          </Box>
                        ) : null}
                      </Paper>
                    </Grid>
                  ))}
                  
                  {/* Botón para agregar Comunidad */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <IconButton
                        size="large"
                        color="primary"
                        onClick={handleAgregarComunidad}
                        sx={{ 
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          width: '100%',
                          height: '100%',
                          minHeight: 200
                        }}
                      >
                        <Add sx={{ fontSize: 40 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
      </LocalizationProvider>
    </Container>
  );

  async function handleFileUpload(file, seccion, tipo) {
    if (!id || !file) {
      console.error('handleFileUpload: id o file faltante', { id, file });
      return;
    }
    
    // Iniciar loading
    setUploading(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [tipo]: true
      }
    }));
    
    try {
      console.log('Subiendo archivo:', { id, seccion, tipo, fileName: file.name });
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('seccion', seccion);
      formData.append('tipo', tipo);
      
      const response = await axios.post(
        `${API_URL}/api/archivos/${id}/subir`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('Respuesta del servidor:', response.data);
      
      if (response.data.success) {
        const archivoData = {
          ruta: response.data.ruta,
          nombre_original: response.data.nombre_original
        };
        
        setArchivos(prev => {
          const nuevosArchivos = {
            ...prev,
            [seccion]: {
              ...prev[seccion],
              [tipo]: archivoData
            }
          };
          
          // Guardar la estructura actualizada con el nuevo estado
          setTimeout(() => guardarArchivos(nuevosArchivos), 100);
          return nuevosArchivos;
        });
      }
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al subir el archivo';
      alert(`Error al subir el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setUploading(prev => ({
        ...prev,
        [seccion]: {
          ...prev[seccion],
          [tipo]: false
        }
      }));
    }
  }

  async function handleDownloadFile(seccion, tipo) {
    const archivoData = archivos[seccion][tipo];
    if (!archivoData || !archivoData.ruta) return;
    
    // Iniciar loading
    setDownloading(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [tipo]: true
      }
    }));
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: archivoData.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = archivoData.nombre_original || 'archivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al descargar el archivo';
      alert(`Error al descargar el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setDownloading(prev => ({
        ...prev,
        [seccion]: {
          ...prev[seccion],
          [tipo]: false
        }
      }));
    }
  }

  async function handleOpenFile(seccion, tipo) {
    const archivoData = archivos[seccion][tipo];
    if (!archivoData || !archivoData.ruta) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: archivoData.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      // No revocamos la URL inmediatamente para que la nueva pestaña pueda cargarla
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error abriendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al abrir el archivo';
      alert(`Error al abrir el archivo: ${errorMessage}`);
    }
  }

  async function handleDeleteFile(seccion, tipo) {
    const archivoData = archivos[seccion][tipo];
    if (!archivoData || !archivoData.ruta) return;
    
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/archivos/${id}/eliminar`, {
        data: { ruta: archivoData.ruta }
      });
      
      setArchivos(prev => {
        const nuevosArchivos = {
          ...prev,
          [seccion]: {
            ...prev[seccion],
            [tipo]: null
          }
        };
        
        // Guardar la estructura actualizada con el nuevo estado
        setTimeout(() => guardarArchivos(nuevosArchivos), 100);
        return nuevosArchivos;
      });
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al eliminar el archivo';
      alert(`Error al eliminar el archivo: ${errorMessage}`);
    }
  }

  async function handleFileUploadOtros(file, otroId) {
    if (!id || !file) return;
    
    // Iniciar loading
    setUploading(prev => ({
      ...prev,
      otros: {
        ...prev.otros,
        [otroId]: true
      }
    }));
    
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('seccion', 'otros');
      formData.append('tipo', otroId);
      
      const response = await axios.post(
        `${API_URL}/api/archivos/${id}/subir`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        const archivoData = {
          ruta: response.data.ruta,
          nombre_original: response.data.nombre_original
        };
        
        setArchivos(prev => {
          const nuevosArchivos = {
            ...prev,
            otros: prev.otros.map(otro => 
              otro.id === otroId ? { ...otro, archivo: archivoData } : otro
            )
          };
          
          // Guardar la estructura actualizada con el nuevo estado
          setTimeout(() => guardarArchivos(nuevosArchivos), 100);
          return nuevosArchivos;
        });
      }
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al subir el archivo';
      alert(`Error al subir el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setUploading(prev => ({
        ...prev,
        otros: {
          ...prev.otros,
          [otroId]: false
        }
      }));
    }
  }

  async function handleDownloadFileOtros(otroId) {
    const otro = archivos.otros.find(o => o.id === otroId);
    if (!otro || !otro.archivo || !otro.archivo.ruta) return;
    
    // Iniciar loading
    setDownloading(prev => ({
      ...prev,
      otros: {
        ...prev.otros,
        [otroId]: true
      }
    }));
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: otro.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = otro.archivo.nombre_original || 'archivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al descargar el archivo';
      alert(`Error al descargar el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setDownloading(prev => ({
        ...prev,
        otros: {
          ...prev.otros,
          [otroId]: false
        }
      }));
    }
  }

  async function handleOpenFileOtros(otroId) {
    const otro = archivos.otros.find(o => o.id === otroId);
    if (!otro || !otro.archivo || !otro.archivo.ruta) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: otro.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error abriendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al abrir el archivo';
      alert(`Error al abrir el archivo: ${errorMessage}`);
    }
  }

  async function handleDeleteFileOtros(otroId) {
    const otro = archivos.otros.find(o => o.id === otroId);
    if (!otro || !otro.archivo || !otro.archivo.ruta) return;
    
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/archivos/${id}/eliminar`, {
        data: { ruta: otro.archivo.ruta }
      });
      
      setArchivos(prev => {
        const nuevosArchivos = {
          ...prev,
          otros: prev.otros.map(otro => 
            otro.id === otroId ? { ...otro, archivo: null } : otro
          )
        };
        
        // Guardar la estructura actualizada con el nuevo estado
        setTimeout(() => guardarArchivos(nuevosArchivos), 100);
        return nuevosArchivos;
      });
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al eliminar el archivo';
      alert(`Error al eliminar el archivo: ${errorMessage}`);
    }
  }

  async function handleFileUploadComunidades(file, comunidadId) {
    if (!id || !file) return;
    
    // Iniciar loading
    setUploading(prev => ({
      ...prev,
      comunidades: {
        ...prev.comunidades,
        [comunidadId]: true
      }
    }));
    
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('seccion', 'comunidades');
      formData.append('tipo', comunidadId);
      
      const response = await axios.post(
        `${API_URL}/api/archivos/${id}/subir`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        const archivoData = {
          ruta: response.data.ruta,
          nombre_original: response.data.nombre_original
        };
        
        setArchivos(prev => {
          const nuevosArchivos = {
            ...prev,
            comunidades: prev.comunidades.map(comunidad => 
              comunidad.id === comunidadId ? { ...comunidad, archivo: archivoData } : comunidad
            )
          };
          
          // Guardar la estructura actualizada con el nuevo estado
          setTimeout(() => guardarArchivos(nuevosArchivos), 100);
          return nuevosArchivos;
        });
      }
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al subir el archivo';
      alert(`Error al subir el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setUploading(prev => ({
        ...prev,
        comunidades: {
          ...prev.comunidades,
          [comunidadId]: false
        }
      }));
    }
  }

  async function handleDownloadFileComunidades(comunidadId) {
    const comunidad = archivos.comunidades.find(c => c.id === comunidadId);
    if (!comunidad || !comunidad.archivo || !comunidad.archivo.ruta) return;
    
    // Iniciar loading
    setDownloading(prev => ({
      ...prev,
      comunidades: {
        ...prev.comunidades,
        [comunidadId]: true
      }
    }));
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: comunidad.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = comunidad.archivo.nombre_original || 'archivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al descargar el archivo';
      alert(`Error al descargar el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setDownloading(prev => ({
        ...prev,
        comunidades: {
          ...prev.comunidades,
          [comunidadId]: false
        }
      }));
    }
  }

  async function handleOpenFileComunidades(comunidadId) {
    const comunidad = archivos.comunidades.find(c => c.id === comunidadId);
    if (!comunidad || !comunidad.archivo || !comunidad.archivo.ruta) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: comunidad.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error abriendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al abrir el archivo';
      alert(`Error al abrir el archivo: ${errorMessage}`);
    }
  }

  async function handleDeleteFileComunidades(comunidadId) {
    const comunidad = archivos.comunidades.find(c => c.id === comunidadId);
    if (!comunidad || !comunidad.archivo || !comunidad.archivo.ruta) return;
    
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/archivos/${id}/eliminar`, {
        data: { ruta: comunidad.archivo.ruta }
      });
      
      setArchivos(prev => {
        const nuevosArchivos = {
          ...prev,
          comunidades: prev.comunidades.map(comunidad => 
            comunidad.id === comunidadId ? { ...comunidad, archivo: null } : comunidad
          )
        };
        
        // Guardar la estructura actualizada con el nuevo estado
        setTimeout(() => guardarArchivos(nuevosArchivos), 100);
        return nuevosArchivos;
      });
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al eliminar el archivo';
      alert(`Error al eliminar el archivo: ${errorMessage}`);
    }
  }

  function handleDeleteComunidad(id) {
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        comunidades: prev.comunidades.filter(comunidad => comunidad.id !== id)
      };
      // Guardar automáticamente cuando se elimina una comunidad
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  function handleUpdateNombreComunidad(id, nombre) {
    setArchivos(prev => ({
      ...prev,
      comunidades: prev.comunidades.map(comunidad => 
        comunidad.id === id ? { ...comunidad, nombre } : comunidad
      )
    }));
    // Guardar automáticamente cuando se actualiza el nombre (con debounce)
    clearTimeout(window.nombreComunidadTimeout);
    window.nombreComunidadTimeout = setTimeout(() => guardarArchivos(), 500);
  }

  function handleAgregarComunidad() {
    const nuevoId = `comunidad_${Date.now()}`;
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        comunidades: [...prev.comunidades, {
          id: nuevoId,
          nombre: '',
          archivo: null,
          esDefault: false
        }]
      };
      // Guardar automáticamente cuando se agrega una comunidad
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  // --- Laboratorio extras (recuadros dinámicos) ---

  async function handleFileUploadLaboratorioExtra(file, extraId) {
    if (!id || !file) return;
    
    // Iniciar loading
    setUploading(prev => ({
      ...prev,
      laboratorio: {
        ...prev.laboratorio,
        [extraId]: true
      }
    }));
    
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('seccion', 'laboratorio_extras');
      formData.append('tipo', extraId);
      
      const response = await axios.post(
        `${API_URL}/api/archivos/${id}/subir`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        const archivoData = {
          ruta: response.data.ruta,
          nombre_original: response.data.nombre_original
        };
        
        setArchivos(prev => {
          const nuevosArchivos = {
            ...prev,
            laboratorio_extras: prev.laboratorio_extras.map(item =>
              item.id === extraId ? { ...item, archivo: archivoData } : item
            )
          };
          // Guardar la estructura actualizada con el nuevo estado
          setTimeout(() => guardarArchivos(nuevosArchivos), 100);
          return nuevosArchivos;
        });
      }
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al subir el archivo';
      alert(`Error al subir el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setUploading(prev => ({
        ...prev,
        laboratorio: {
          ...prev.laboratorio,
          [extraId]: false
        }
      }));
    }
  }

  async function handleDownloadFileLaboratorioExtra(extraId) {
    const item = archivos.laboratorio_extras.find(i => i.id === extraId);
    if (!item || !item.archivo || !item.archivo.ruta) return;
    
    // Iniciar loading
    setDownloading(prev => ({
      ...prev,
      laboratorio: {
        ...prev.laboratorio,
        [extraId]: true
      }
    }));
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: item.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = item.archivo.nombre_original || 'archivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al descargar el archivo';
      alert(`Error al descargar el archivo: ${errorMessage}`);
    } finally {
      // Terminar loading
      setDownloading(prev => ({
        ...prev,
        laboratorio: {
          ...prev.laboratorio,
          [extraId]: false
        }
      }));
    }
  }

  async function handleOpenFileLaboratorioExtra(extraId) {
    const item = archivos.laboratorio_extras.find(i => i.id === extraId);
    if (!item || !item.archivo || !item.archivo.ruta) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/archivos/${id}/descargar`,
        {
          params: { ruta: item.archivo.ruta },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error abriendo archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al abrir el archivo';
      alert(`Error al abrir el archivo: ${errorMessage}`);
    }
  }

  async function handleDeleteFileLaboratorioExtra(extraId) {
    const item = archivos.laboratorio_extras.find(i => i.id === extraId);
    if (!item || !item.archivo || !item.archivo.ruta) return;
    
    if (!window.confirm('¿Estás seguro de eliminar este archivo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/archivos/${id}/eliminar`, {
        data: { ruta: item.archivo.ruta }
      });
      
      setArchivos(prev => {
        const nuevosArchivos = {
          ...prev,
          laboratorio_extras: prev.laboratorio_extras.map(i =>
            i.id === extraId ? { ...i, archivo: null } : i
          )
        };
        // Guardar la estructura actualizada con el nuevo estado
        setTimeout(() => guardarArchivos(nuevosArchivos), 100);
        return nuevosArchivos;
      });
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al eliminar el archivo';
      alert(`Error al eliminar el archivo: ${errorMessage}`);
    }
  }

  function handleAgregarLaboratorioExtra() {
    const nuevoId = `laboratorio_extra_${Date.now()}`;
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        laboratorio_extras: [
          ...prev.laboratorio_extras,
          {
            id: nuevoId,
            nombre: '',
            archivo: null,
            esDefault: false
          }
        ]
      };
      // Guardar automáticamente cuando se agrega un recuadro
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  function handleDeleteLaboratorioExtra(id) {
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        laboratorio_extras: prev.laboratorio_extras.filter(item => item.id !== id)
      };
      // Guardar automáticamente cuando se elimina un recuadro
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  function handleUpdateNombreLaboratorioExtra(id, nombre) {
    setArchivos(prev => ({
      ...prev,
      laboratorio_extras: prev.laboratorio_extras.map(item =>
        item.id === id ? { ...item, nombre } : item
      )
    }));
    // Guardar automáticamente cuando se actualiza el nombre (con debounce)
    clearTimeout(window.nombreLaboratorioExtraTimeout);
    window.nombreLaboratorioExtraTimeout = setTimeout(() => guardarArchivos(), 500);
  }

  function handleAgregarOtro() {
    const nuevoId = `otro_${Date.now()}`;
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        otros: [...prev.otros, {
          id: nuevoId,
          nombre: '',
          archivo: null,
          esDefault: false
        }]
      };
      // Guardar automáticamente cuando se agrega un recuadro en Otros
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  function handleDeleteOtro(id) {
    setArchivos(prev => {
      const nuevosArchivos = {
        ...prev,
        otros: prev.otros.filter(otro => otro.id !== id)
      };
      // Guardar automáticamente cuando se elimina un recuadro en Otros
      setTimeout(() => guardarArchivos(nuevosArchivos), 100);
      return nuevosArchivos;
    });
  }

  function handleUpdateNombreOtro(id, nombre) {
    setArchivos(prev => ({
      ...prev,
      otros: prev.otros.map(otro => 
        otro.id === id ? { ...otro, nombre } : otro
      )
    }));
    // Guardar automáticamente cuando se actualiza el nombre (con debounce)
    clearTimeout(window.nombreOtroTimeout);
    window.nombreOtroTimeout = setTimeout(() => guardarArchivos(), 500);
  }

  async function guardarArchivos(archivosParaGuardar = null) {
    if (!propiedad || !id) return;
    
    try {
      // Usar los archivos pasados como parámetro o el estado actual
      const archivosActuales = archivosParaGuardar || archivos;
      
      // Preparar datos de archivos (guardando las rutas de los archivos subidos)
      const archivosData = {
        laboratorio: archivosActuales.laboratorio,
        laboratorio_extras: (archivosActuales.laboratorio_extras || []).map(item => ({
          id: item.id,
          nombre: item.nombre,
          archivo: item.archivo || null,
          esDefault: item.esDefault
        })),
        otros: archivosActuales.otros.map(otro => ({
          id: otro.id,
          nombre: otro.nombre,
          archivo: otro.archivo || null, // Guardar la ruta del archivo si existe
          esDefault: otro.esDefault
        })),
        invitaciones: archivosActuales.invitaciones,
        comunidades: archivosActuales.comunidades.map(comunidad => ({
          id: comunidad.id,
          nombre: comunidad.nombre,
          archivo: comunidad.archivo || null, // Guardar la ruta del archivo si existe
          esDefault: comunidad.esDefault
        }))
      };

      console.log('Guardando archivos:', archivosData);

      const response = await axios.put(`${API_URL}/api/propiedades/${id}`, {
        nombre: propiedad.nombre,
        presupuesto: propiedad.presupuesto || {},
        fecha_presentacion: propiedad.fecha_presentacion,
        fecha_programada: propiedad.fecha_programada,
        laboratorio: propiedad.laboratorio || {},
        adelantos: propiedad.adelantos || [],
        muestras: propiedad.muestras || {},
        archivos: archivosData
      });
      
      console.log('Archivos guardados exitosamente:', response.data.archivos);
      
      // Actualizar el estado de propiedad con los datos guardados
      setPropiedad(response.data);
    } catch (error) {
      console.error('Error guardando archivos:', error);
      alert('Error al guardar los archivos. Por favor, intenta nuevamente.');
    }
  }

  function handleAgregarMuestra() {
    if (nuevaMuestra.nombre.trim()) {
      setMuestras({
        ...muestras,
        otras: [...muestras.otras, {
          nombre: nuevaMuestra.nombre.trim(),
          cantidad: nuevaMuestra.cantidad || '0'
        }]
      });
      setNuevaMuestra({ nombre: '', cantidad: '' });
      setDialogMuestraAbierto(false);
    }
  }
}

export default PropiedadDetail;

